export type WorstBestEnvelopePoint = {
  worst: number | null;
  best: number | null;
};

export function computeWorstBestEnvelope(
  segmentScoresByName: Record<string, Array<number | null>>,
  bucketCount: number,
): WorstBestEnvelopePoint[] {
  return Array.from({ length: bucketCount }, (_, index) => {
    const values = Object.values(segmentScoresByName)
      .map((series) => series[index] ?? null)
      .filter((value): value is number => value != null && Number.isFinite(value));

    if (values.length === 0) return { worst: null, best: null };
    return { worst: Math.min(...values), best: Math.max(...values) };
  });
}

/** Linearly interpolate worst/best across buckets with no segment data so the envelope spans the chart. */
export function inferWorstBestEnvelope(envelope: WorstBestEnvelopePoint[]): WorstBestEnvelopePoint[] {
  if (envelope.length === 0) return [];

  const known = envelope
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => point.worst != null && point.best != null);

  if (known.length === 0) return envelope;

  const nearestKnownEnvelope = (index: number): WorstBestEnvelopePoint => {
    const sorted = [...known].sort(
      (left, right) => Math.abs(left.index - index) - Math.abs(right.index - index),
    );

    for (const candidate of sorted) {
      const spread = candidate.point.best! - candidate.point.worst!;
      if (spread > 0) return candidate.point;
    }

    return sorted[0].point;
  };

  return envelope.map((point, index) => {
    if (point.worst != null && point.best != null) return point;
    return nearestKnownEnvelope(index);
  });
}
